import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { defineMessages, useIntl } from 'react-intl';

import AdvancedHotkey from './component';
import Global from '../../../store/global-selectors';
import { purchaseBulkLabels } from '../../../store/orders/orders.actions';
import { useInventoryAllocation } from '../../../hooks/inventory/use-inventory-allocation';
import { usePrepayDutiesDataFromEntities } from '../../../hooks/entities/selectors/fulfillment-plans';
import PrepayDutyPromptOnAction from '../../../fulfillment-planner/duties-and-taxes/prompt-on-action';
import { useIsAccountEmailVerifiedSellerSetting } from '../../../hooks/use-seller-setting';
import { useModal } from '../../../hooks/use-modal';
import { VerifyEmailModal } from '../../../components/verify-email-modal';
import { CREATE_LABEL } from '../../../constants/email-resend-source';

export const messages = defineMessages({
  name: {
    description: 'create label hotkey',
    defaultMessage: 'Create label (Ship Order)',
    id: 'gPlR7L',
  },
});

export const hotkey = ['s'];
export const barcodeContent = '+0EEB8D3F358D=';

const CreateLabelHotkey = ({ children, disabled, selectedFulfillmentPlanIds }) => {
  const intl = useIntl();
  const { allocateInventory } = useInventoryAllocation();
  const selectSelectedOrdersIds = useSelector(Global.selectSelectedOrdersIds);
  const fulfillmentPlanIds = selectedFulfillmentPlanIds || selectSelectedOrdersIds;
  const { availableDutiesOptions, otherShippingOptions, onSelectedDutyOptionChange } = usePrepayDutiesDataFromEntities(fulfillmentPlanIds);

  const isAccountEmailVerified = useIsAccountEmailVerifiedSellerSetting();

  const { close: closeUnverifiedModal, isOpen: isUnverifiedModalOpen, open: openUnverifiedModal } = useModal();

  const onUpdate = useCallback(() => {
    if (!isAccountEmailVerified) {
      openUnverifiedModal();
      return;
    }
    // TODO: Add a check to see if the seller's wallet is setup or not - Greg S. 10/1/24
    allocateInventory(fulfillmentPlanIds, purchaseBulkLabels(false, fulfillmentPlanIds));
  }, [allocateInventory, fulfillmentPlanIds, isAccountEmailVerified, openUnverifiedModal]);
  const hotkeyMetadata = useMemo(
    () => ({
      barcode: barcodeContent,
      displayHotkeyStrings: hotkey,
      hotkey,
      handler: onUpdate,
      name: intl.formatMessage(messages.name),
      legacyCategory: 'general',
      doNotTrack: true,
    }),
    [intl, onUpdate],
  );

  const _onSelectedDutyOptionChange = useCallback(
    (selectedDutyOption) => {
      onSelectedDutyOptionChange(fulfillmentPlanIds[0], selectedDutyOption);
    },
    [fulfillmentPlanIds, onSelectedDutyOptionChange],
  );

  return (
    <>
      {!disabled && (
        <PrepayDutyPromptOnAction
          availableDutiesOptions={availableDutiesOptions}
          canShowPrompt={fulfillmentPlanIds?.length === 1}
          onContinueToPurchase={onUpdate}
          onSelectedDutyOptionChange={_onSelectedDutyOptionChange}
          otherShippingOptions={otherShippingOptions}
        >
          {({ onActionTaken }) => {
            hotkeyMetadata.handler = onActionTaken;
            return <AdvancedHotkey hotkey={hotkeyMetadata} />;
          }}
        </PrepayDutyPromptOnAction>
      )}
      {children}
      {isUnverifiedModalOpen && <VerifyEmailModal closeUnverifiedModal={closeUnverifiedModal} source={CREATE_LABEL} />}
    </>
  );
};

CreateLabelHotkey.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  disabled: PropTypes.bool.isRequired,
  selectedFulfillmentPlanIds: PropTypes.arrayOf(PropTypes.string),
};

export default CreateLabelHotkey;
